import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import {
  PrintProofDto,
  PrintProofReviewDto,
  PrintProofReviewResult,
  PrintProofStatus,
} from '@/api-sdk';

import { FgDialog, FgPrintProofReject } from '@/components';
import { GlobalComponentType, global } from '@/context';
import { BREAKPOINT_LARGE_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';
import { handlePrintProofReview } from './utils';

import '@/components/fg-checkbox';
import '@/components/fg-dialog';
import './fg-print-proof-reject';
import './status/modal/fg-print-proof-status-modal-reviewed';

@customElement('fg-print-proof')
@localized()
export class FgPrintProof extends LitElement {
  private fgDialogRef: Ref<FgDialog> = createRef();

  private fgPrintProofRejectDialogRef: Ref<FgPrintProofReject> = createRef();

  @property({ type: Object })
  printProof?: PrintProofDto | null;

  @property({ type: Boolean })
  termsAccepted = false;

  @property({ type: String })
  configurationId = '';

  @property({ type: String })
  logoUrl = '';

  @property({ type: String })
  storeCode?: string;

  @state()
  loading = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  private async approvePrintProof() {
    if (!this.storeCode) {
      console.error('CC-FE: Store code is not set.');
      return;
    }

    const reviewData: PrintProofReviewDto = {
      result: PrintProofReviewResult.APPROVED,
      feedback: null,
    };

    this.loading = true;

    await handlePrintProofReview(
      this.configurationId,
      reviewData,
      this,
      this.storeCode,
    );

    this.loading = false;
  }

  render() {
    if (!this.printProof) {
      return null;
    }
    const { review, status, file } = this.printProof;

    if (!file) {
      return null;
    }

    const { layout } = this.globalContext;

    return html`
      <fg-dialog fullscreen ${ref(this.fgDialogRef)}>
        <span class="title" slot="title">
          ${t('content.printProof.reviewPrintProof')}
        </span>

        <main>
          <iframe src=${`${file.pdfUrl}#toolbar=0`}></iframe>

          <aside>
            ${this.logoUrl
              ? html`
                  <img class="logo" src=${this.logoUrl} alt="" />
                `
              : nothing}

            <div class="information">
              <p>
                <strong>${t('content.printProof.importantInformation')}</strong>
              </p>

              ${unsafeHTML(
                t(
                  layout === 'retail'
                    ? 'content.printProof.importantInformationListRetail'
                    : 'content.printProof.importantInformationListWholesale',
                ),
              )}
            </div>

            <a
              href=${file.pdfUrl}
              class="button button-primary view-print-proof"
              target="_blank"
            >
              ${t('content.printProof.viewPrintProof')}
            </a>

            ${status === PrintProofStatus.REVIEWED
              ? html`
                  <fg-print-proof-status-modal-reviewed
                    part=${layout}
                    .status=${review?.result}
                    @close-print-proof-dialog=${this.closeDialog}
                  ></fg-print-proof-status-modal-reviewed>
                `
              : html`
                  <div class="accept-print-proof">
                    <label @click=${this.toggleTermsAccepted}>
                      <div
                        class=${`checkbox ${
                          this.termsAccepted ? 'checked' : ''
                        }`}
                      >
                        <fg-checkbox
                          .checked=${this.termsAccepted}
                        ></fg-checkbox>
                      </div>

                      ${t('content.printProof.terms')}
                    </label>

                    <button
                      class="button ${classMap({
                        'button-primary': layout === 'wholesale',
                        'button-cta': layout === 'retail',
                      })}"
                      ?disabled=${!this.termsAccepted || this.loading}
                      @click=${this.approvePrintProof}
                    >
                      ${t('content.printProof.acceptPrintProof')}
                    </button>
                  </div>

                  <div class="reject-print-proof">
                    <button
                      @click=${this.openPrintProofRejectDialog}
                      class="button-link"
                    >
                      ${t('content.printProof.rejectPrintProof')}
                    </button>
                  </div>
                `}
          </aside>
        </main>

        <fg-print-proof-reject
          ${ref(this.fgPrintProofRejectDialogRef)}
          .configurationId=${this.configurationId}
          .storeCode=${this.storeCode}
        ></fg-print-proof-reject>
      </fg-dialog>
    `;
  }

  toggleTermsAccepted() {
    this.termsAccepted = !this.termsAccepted;
  }

  openPrintProofRejectDialog() {
    this.fgPrintProofRejectDialogRef.value?.openDialog();
  }

  openDialog() {
    this.fgDialogRef.value?.open();
  }

  closeDialog() {
    this.fgDialogRef.value?.close();
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .title,
      .view-print-proof {
        display: none;
      }

      aside {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .logo {
        max-width: 114px;
      }

      .information,
      .accept-print-proof {
        line-height: 1.4;
      }

      :host([part='wholesale']) .information,
      :host([part='wholesale']) .accept-print-proof {
        font-size: 1rem;
      }

      .information ul {
        padding-left: 15px;
      }

      .information ul li {
        list-style: outside disc;
      }

      .accept-print-proof label {
        display: flex;
        gap: 8px;
        cursor: pointer;
      }

      :host([part='retail']) .accept-print-proof .checkbox {
        color: var(--fg-gray--500, var(--fg-fallback-gray--500));
      }

      :host([part='retail']) .accept-print-proof .checkbox.checked {
        color: var(--fg-cta, var(--fg-fallback-cta));
      }

      :host([part='wholesale']) .accept-print-proof .checkbox.checked {
        color: var(--fg-success--dark, var(--fg-success--dark));
      }

      .accept-print-proof button {
        width: 100%;
        margin-top: 20px;
      }

      .reject-print-proof {
        text-align: center;
      }

      .reject-print-proof button {
        color: var(--fg-error--dark, var(--fg-fallback-error--dark));
      }

      @media screen and (max-width: ${BREAKPOINT_LARGE_PX - 1}px) {
        iframe {
          display: none;
        }

        .title {
          display: block;
        }

        .view-print-proof {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .accept-print-proof {
          border-top: 1px solid
            var(--fg-gray--300, var(--fg-fallback-gray--300));
          padding-top: 20px;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        main {
          display: grid;
          grid-template-columns: 1fr 300px;
          height: 100%;
        }

        aside {
          gap: 25px;
          padding-left: 20px;
        }

        .logo {
          max-width: 170px;
        }

        iframe {
          width: 100%;
          height: 100%;
          border: 0;
        }
      }
    `,
  ];
}
